import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Save, ArrowLeft, Sparkles } from 'lucide-react';
import {
  announcementsService,
  AnnouncementType,
  ANNOUNCEMENT_TYPE_LABELS,
  ANNOUNCEMENT_KIND_LABELS,
  ANNOUNCEMENT_ROLE_ORDER,
  ANNOUNCEMENT_ROLE_LABELS,
  ANNOUNCEMENT_CONTENT_MAX,
} from '../../services/announcementsService';
import { UserRole } from '../../types';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { useAuthStore } from '../../store/authStore';

export const AnnouncementFormPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEdit = !!id;
  // Šablona novinky o vydání je věc správce aplikace, schvalovatel ji nepotřebuje.
  const jeAdmin = useAuthStore((s) => s.user?.role === 'admin');

  const [formData, setFormData] = useState<{
    title: string;
    content: string;
    type: AnnouncementType;
    kind: 'manual' | 'version';
    priority: number;
    expiresAt: string;
    targetRoles: UserRole[];
  }>({
    title: '',
    content: '',
    type: 'info',
    kind: 'manual',
    priority: 5,
    expiresAt: '',
    targetRoles: [],
  });

  // Prazdne targetRoles = novinka pro vsechny; prepinac je proto zvlast, aby slo
  // vybrat "jen nekterym" a teprve pak zaskrtavat role.
  const [cileni, setCileni] = useState<'vsem' | 'vybrane'>('vsem');

  const prepniRoli = (role: UserRole) =>
    setFormData((d) => ({
      ...d,
      targetRoles: d.targetRoles.includes(role)
        ? d.targetRoles.filter((r) => r !== role)
        : [...d.targetRoles, role],
    }));

  const { data: announcement } = useQuery({
    queryKey: ['announcements', id],
    queryFn: () => announcementsService.getAnnouncement(id!),
    enabled: isEdit,
  });

  useEffect(() => {
    if (announcement) {
      setFormData({
        title: announcement.title,
        content: announcement.content,
        type: announcement.type,
        // Novinku o letáku zakládá aplikace sama; ve výběru takový druh není,
        // tak ji nepřepisujeme na "Sdělení" jen tím, že se otevře formulář.
        kind: announcement.kind === 'version' ? 'version' : 'manual',
        priority: announcement.priority,
        expiresAt: announcement.expiresAt ? announcement.expiresAt.split('T')[0] : '',
        targetRoles: announcement.targetRoles ?? [],
      });
      setCileni(announcement.targetRoles?.length ? 'vybrane' : 'vsem');
    }
  }, [announcement]);

  const saveMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const payload = {
        title: data.title,
        content: data.content,
        type: data.type,
        kind: data.kind,
        priority: data.priority,
        expiresAt: data.expiresAt || undefined,
        targetRoles: cileni === 'vsem' ? [] : data.targetRoles,
      };
      return isEdit && id
        ? announcementsService.updateAnnouncement(id, payload)
        : announcementsService.createAnnouncement(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['announcements'] });
      navigate('/announcements');
    },
    onError: (error: any) => {
      alert(error.response?.data?.message || 'Chyba při ukládání novinky');
    },
  });

  const SABLONA_VERZE = {
    title: 'Nová verze aplikace [VERSION]',
    // Šablonový řetězec kvůli odřádkování: prázdný řádek a pak první odrážka.
    content: `Nová funkcionalita:

- `,
  };

  /**
   * Předvyplní novinku o novém vydání. Značka [VERSION] se v názvu i textu
   * nahradí číslem verze až ve chvíli zveřejnění - do té doby jde novinku
   * upravovat a na čísle nezáleží.
   */
  const pouzitSablonuVerze = () => {
    const prepiseObsah = formData.title.trim() !== '' || formData.content.trim() !== '';
    if (prepiseObsah && !window.confirm('Přepsat název i text připravenou šablonou?')) return;

    setFormData((d) => ({ ...d, ...SABLONA_VERZE, kind: 'version' }));
    setCileni('vsem');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (cileni === 'vybrane' && formData.targetRoles.length === 0) {
      alert('Vyberte alespoň jednu roli, nebo zvolte „Všem rolím".');
      return;
    }
    saveMutation.mutate(formData);
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
      <div className="mb-6">
        <Button variant="outline" onClick={() => navigate('/announcements')} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Zpět na novinky
        </Button>
        <div className="flex items-start justify-between gap-4">
          <h1 className="text-3xl font-bold text-gray-900">
            {isEdit ? 'Upravit novinku' : 'Nová novinka'}
          </h1>
          {jeAdmin && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={pouzitSablonuVerze}
              title="Předvyplní novinku o novém vydání. Číslo verze se doplní až při zveřejnění."
              className="flex-shrink-0"
            >
              <Sparkles className="w-4 h-4 mr-1" />
              Šablona: nová verze
            </Button>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="bg-white rounded-lg shadow p-6 space-y-6">
          <Input
            label="Název *"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            required
            maxLength={200}
            placeholder="Například: Důležitá aktualizace systému"
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Obsah *</label>
            <textarea
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              required
              maxLength={ANNOUNCEMENT_CONTENT_MAX}
              placeholder="Text novinky…"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[120px]"
            />
            <p className="mt-1 text-xs text-gray-500 text-right">
              {formData.content.length} / {ANNOUNCEMENT_CONTENT_MAX} znaků
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Zařazení</label>
              <select
                value={formData.kind}
                onChange={(e) => setFormData({ ...formData, kind: e.target.value as 'manual' | 'version' })}
                title="Podle zařazení se novinka objeví ve filtru v přehledu novinek."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {(Object.keys(ANNOUNCEMENT_KIND_LABELS) as ('manual' | 'version')[]).map((k) => (
                  <option key={k} value={k}>
                    {ANNOUNCEMENT_KIND_LABELS[k]}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Typ</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value as AnnouncementType })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {(Object.keys(ANNOUNCEMENT_TYPE_LABELS) as AnnouncementType[]).map((t) => (
                  <option key={t} value={t}>
                    {ANNOUNCEMENT_TYPE_LABELS[t]}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Input
                label="Priorita (0–10)"
                type="number"
                min={0}
                max={10}
                value={formData.priority}
                onChange={(e) =>
                  setFormData({ ...formData, priority: parseInt(e.target.value, 10) || 0 })
                }
              />
            </div>

            <div>
              <Input
                label="Vypršení (volitelné)"
                type="date"
                value={formData.expiresAt}
                onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })}
              />
            </div>
          </div>
          <p className="text-sm text-gray-500">
            Bez data vypršení se novinka zobrazuje 14 dní od vytvoření.
          </p>

          <div className="border-t border-gray-200 pt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Komu se novinka zobrazí</label>
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="radio"
                  name="cileni"
                  checked={cileni === 'vsem'}
                  onChange={() => setCileni('vsem')}
                />
                Všem rolím
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="radio"
                  name="cileni"
                  checked={cileni === 'vybrane'}
                  onChange={() => setCileni('vybrane')}
                />
                Jen vybraným rolím
              </label>
            </div>

            {cileni === 'vybrane' && (
              <div className="mt-3 ml-6 flex flex-wrap gap-x-6 gap-y-2">
                {ANNOUNCEMENT_ROLE_ORDER.map((r) => (
                  <label key={r} className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={formData.targetRoles.includes(r)}
                      onChange={() => prepniRoli(r)}
                    />
                    {ANNOUNCEMENT_ROLE_LABELS[r]}
                  </label>
                ))}
              </div>
            )}

            <p className="mt-3 text-sm text-gray-500">
              Cílení platí pro zobrazení novinky uživatelům. Ve správě novinek vidí admin
              i schvalovatel všechny novinky bez ohledu na cílení.
            </p>
          </div>

          <div className="mt-2 flex justify-end space-x-3">
            <Button type="button" variant="outline" onClick={() => navigate('/announcements')}>
              Zrušit
            </Button>
            <Button type="submit" disabled={saveMutation.isPending}>
              <Save className="w-4 h-4 mr-2" />
              {saveMutation.isPending ? 'Ukládám...' : 'Uložit'}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};
